import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { MultiLineText } from "@/components/views/MultiLineText";
import { PageHeader } from "@/components/views/PageHeader";
import { formatLocaleNettoPoints, formatLocaleNumber } from "@/utils/formatUtils";
import { useBackNavigation } from "@/utils/hooksUtils";
import { tBoolean } from "@/utils/translationUtils";
import { cssVariables, isDefined } from "@/utils/utils";
import { Button, Card, Descriptions, Flex, Popconfirm, Space } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteCommissionsSettingsLevel } from "../../../../../../common/api/queries";
import { Permission } from "../../../../../../common/security/enums";
import { useHasPermissions } from "../../../../../auth/hooks";
import { BailAccountSettingsForm } from "../../../../bailaccount/components/forms/BailAccountSettingsForm";
import { BailAccountSettingsView } from "../../../../bailaccount/components/views/BailAccountSettingsView";
import { CommissionsSettingsRuleHelperView } from "../../../../settings/components/views/CommissionsSettingsRuleHelperView";
import { CommissionsSettingsLevelType } from "../../../enums";
import { COMMISSIONS_LEVEL_ROUTE_PATHS } from "../../../paths";
import { CommissionsSettingsLevel, CommissionsSettingsLevelRule } from "../../../types";
import { formatCommissionsLevelName } from "../../../utils";
import CommissionsLevelTypeTag from "../../CommissionsLevelTypeTag";
import { CommissionsLevelForm } from "../../forms/CommissionsLevelForm";
import { CommissionsLevelRuleForm } from "../../forms/CommissionsLevelRuleForm";
import CommissionsLevelAttachmentsTableView from "./CommissionsLevelAttachmentsTableView";
import { CommissionsLevelRuleTableView } from "./CommissionsLevelRuleTableView";

interface Props {
  level: CommissionsSettingsLevel;
}

export const CommissionsLevelDetailView = ({ level }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const onBackClick = useBackNavigation();

  const hasTopAgentCommissionsManagePermission = useHasPermissions(Permission.TOP_AGENT_COMMISSIONS_MANAGE);

  const deleteCommissionsSettingsLevel = useDeleteCommissionsSettingsLevel();

  const [levelFormOpen, setLevelFormOpen] = useState(false);

  const [ruleFormOpen, setRuleFormOpen] = useState(false);
  const [ruleToUpdate, setRuleToUpdate] = useState<CommissionsSettingsLevelRule>();

  const [bailAccountSettingsFormOpen, setBailAccountSettingsFormOpen] = useState(false);

  const handleRuleUpdateClick = (rule: CommissionsSettingsLevelRule) => {
    setRuleFormOpen(true);
    setRuleToUpdate(rule);
  };

  const handleRuleFormCancel = () => {
    setRuleFormOpen(false);
    setRuleToUpdate(undefined);
  };

  return (
    <>
      <PageHeader
        title={`${t("commissions.level.titles.detail")} ${formatCommissionsLevelName(level)}`}
        tags={<CommissionsLevelTypeTag type={level.type} />}
        onBack={onBackClick}
        extra={
          hasTopAgentCommissionsManagePermission && (
            <Space>
              <Button type="primary" icon={<AntIcon type="edit" />} onClick={() => setLevelFormOpen(true)}>
                {t("common.edit")}
              </Button>

              <Popconfirm
                title={t("commissions.level.helpers.deleteLevelConfirm")}
                icon={<PopconfirmDeleteIcon />}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                okType="danger"
                onConfirm={() =>
                  deleteCommissionsSettingsLevel.mutate(level.id, {
                    onSuccess: () => {
                      navigate(COMMISSIONS_LEVEL_ROUTE_PATHS.list.to, { replace: true });
                    },
                  })
                }
              >
                <Button danger icon={<AntIcon type="delete" />}>
                  {t("common.delete")}
                </Button>
              </Popconfirm>
            </Space>
          )
        }
      >
        <ItemCreatedUpdatedInfoView item={level} style={{ marginTop: "-15px" }} />

        <Flex gap={48}>
          <Descriptions
            className="margin-top-small"
            title={t("commissions.level.sections.commonData")}
            styles={{
              label: { color: cssVariables.colorText },
              root: { display: "inline-block", width: "auto" },
              content: { textAlign: "right" },
            }}
            size="small"
            bordered
            items={[
              {
                key: "category",
                label: t("commissions.level.attrs.category"),
                children: level.category,
              },
              {
                key: "targetSubordinatesCount",
                label: t("commissions.level.attrs.targetSubordinatesCount"),
                children: isDefined(level.targetSubordinatesCount)
                  ? formatLocaleNumber(level.targetSubordinatesCount)
                  : undefined,
              },
              {
                key: "autoAssigned",
                label: (
                  <LabelWithTooltip
                    label={t("commissions.level.attrs.autoAssigned")}
                    tooltip={t("commissions.level.helpers.autoAssignedHint")}
                  />
                ),
                children: tBoolean(level.autoAssigned),
              },
              ...(level.type !== CommissionsSettingsLevelType.BASIC
                ? [
                    {
                      key: "nextLevelId",
                      label: t("commissions.level.attrs.nextLevelId"),
                      children: <span>{formatCommissionsLevelName(level.nextLevel)}</span>,
                    },
                    {
                      key: "formerLevels",
                      label: t("commissions.level.helpers.formerLevels"),
                      children: isDefined(level.formerLevels?.length) && (
                        <MultiLineText
                          tokens={level.formerLevels.map((formerLevel) => formatCommissionsLevelName(formerLevel))}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
            column={1}
          />

          {level.type === CommissionsSettingsLevelType.CAREER && (
            <Descriptions
              title={t("commissions.level.sections.promotionParameters")}
              className="margin-top-small"
              styles={{
                label: { color: cssVariables.colorText },
                root: { display: "inline-block", width: "auto" },
                content: { textAlign: "right" },
              }}
              size="small"
              bordered
              items={[
                {
                  key: "promotionPoints",
                  label: t("commissions.level.attrs.promotionPoints"),
                  children: isDefined(level.promotionPoints) && formatLocaleNettoPoints(level.promotionPoints, 0),
                },
                {
                  key: "promotionPointsRatio",
                  label: t("commissions.level.attrs.promotionPointsRatio"),
                  children:
                    isDefined(level.promotionPointsRatio1) &&
                    `${level.promotionPointsRatio1}/${level.promotionPointsRatio2}`,
                },
                {
                  key: "promotionFinancialAcademyRequired",
                  label: t("commissions.level.attrs.promotionFinancialAcademyRequired"),
                  children: level.promotionFinancialAcademyRequired ? t("common.yes") : t("common.no"),
                },
                {
                  key: "promotionPersonalAcademyRequired",
                  label: t("commissions.level.attrs.promotionPersonalAcademyRequired"),
                  children: level.promotionPersonalAcademyRequired ? t("common.yes") : t("common.no"),
                },
                {
                  key: "promotionBrokerStatusRequired",
                  label: t("commissions.level.attrs.promotionBrokerStatusRequired"),
                  children: level.promotionBrokerStatusRequired ? t("common.yes") : t("common.no"),
                },
                {
                  key: "requiredPromotionRank",
                  label: t("commissions.level.attrs.requiredPromotionRank"),
                  children: level.requiredPromotionRank && level.requiredPromotionRank.name,
                },
              ]}
              column={1}
            />
          )}
        </Flex>
      </PageHeader>

      <Card type="inner" className="card-box" title={t("commissions.level.sections.rules")}>
        <CommissionsLevelRuleTableView
          level={level}
          showActions={hasTopAgentCommissionsManagePermission}
          onUpdateClick={handleRuleUpdateClick}
        />

        {hasTopAgentCommissionsManagePermission && (
          <div className={level.rules?.length ? "table-footer-margin" : "margin-top-small"}>
            <Space>
              <Button type="primary" size="small" icon={<AntIcon type="plus" />} onClick={() => setRuleFormOpen(true)}>
                {t("commissions.level.actions.addRule")}
              </Button>
              <CommissionsSettingsRuleHelperView />
            </Space>
          </div>
        )}
      </Card>

      <Card type="inner" className="card-box" title={t("commissions.level.sections.bailAccountSettings")}>
        <BailAccountSettingsView
          settings={level.bailAccountSettings}
          showActions={hasTopAgentCommissionsManagePermission}
          noSettingsLabel={t("commissions.level.helpers.bailAccount.noSettings")}
          onCreateUpdateClick={() => setBailAccountSettingsFormOpen(true)}
          viewType="commissions-level"
          parentId={level.id}
        />
      </Card>

      <Card type="inner" className="card-box" title={t("commissions.level.sections.documentsAndAttachments")}>
        <CommissionsLevelAttachmentsTableView level={level} />
      </Card>

      <CommissionsLevelForm open={levelFormOpen} level={level} onFormCancel={() => setLevelFormOpen(false)} />

      <CommissionsLevelRuleForm
        open={ruleFormOpen}
        rule={ruleToUpdate}
        levelId={level.id}
        onFormCancel={handleRuleFormCancel}
      />

      <BailAccountSettingsForm
        settings={level.bailAccountSettings}
        open={bailAccountSettingsFormOpen}
        onFormCancel={() => setBailAccountSettingsFormOpen(false)}
        viewType="commissions-level"
        parentId={level.id}
      />
    </>
  );
};
